/**
 * U-01 «Мой профиль».
 *
 * Композиция — профиль человека в Telegram: крупный аватар, имя, статус
 * строкой, ряд плиток-действий и карточка «подпись сверху, значение снизу».
 * Правка живёт в шапке («Изменить»), а не отдельной плиткой и не футером.
 *
 * Изменение v1.5: блоки общие с P-04 и U-02 (`entities/user/ProfileView`) —
 * свой профиль и профиль другого человека выглядят одинаково.
 *
 * ПС §07: показать пользователю, как его видят другие, и дать быстрый вход
 * в редактирование. Карточка статуса (`VisibilityCard`) появляется только
 * когда есть что исправить: у видимой анкеты это просто строка под именем.
 *
 * Изменение v1.12: параметров подбора здесь больше нет — ни плитки, ни
 * строки со сводкой. Всё про подбор живёт в своей вкладке (P-13 «Подбор»),
 * и профиль отвечает только за анкету. Карточка статуса при нужде уводит
 * туда сама.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { PHOTO_REJECTED_LABEL, photoRejectionText } from '@entities/user/photo-state'
import {
  ProfileAction,
  ProfileActions,
  ProfileChips,
  ProfileHero,
  ProfileInfo,
  ProfileInfoRow,
  ProfileSection,
} from '@entities/user/ProfileView'
import { profileVisibility } from '@entities/user/visibility'
import { ActionItem, ActionList, Button, Chip, Icon, Sheet, cx, useToast } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { useAppStore } from '../../model/app-store'
import { VisibilityCard } from './VisibilityCard'
import styles from './profile.module.css'

/**
 * Подсказки заполненности (BS §17.4, `completeness.hints`): `add_photos`,
 * `add_bio`, `add_interests`, `add_goals`, `add_preferences`. Неизвестный код
 * (открытый перечень) показывается нейтральной подсказкой, а не сырой строкой.
 */
const HINT_TEXT: Record<string, string> = {
  add_photos: 'Добавить ещё фото',
  add_photo: 'Добавить фото',
  add_bio: 'Рассказать о себе',
  extend_bio: 'Дописать «о себе»',
  add_interests: 'Отметить интересы',
  add_occupation: 'Указать род занятий',
  add_languages: 'Добавить языки',
  set_city: 'Указать город',
  add_goals: 'Уточнить, чего ищете',
  add_preferences: 'Заполнить параметры подбора',
}

const HINT_FALLBACK = 'Дополнить профиль'

function hintText(hint: string): string {
  return HINT_TEXT[hint] ?? HINT_FALLBACK
}

/** U-08: каждая подсказка ведёт к тому блоку, который её снимает. */
const HINT_TARGET: Record<string, '/me/edit' | '/me/photos' | '/dvizh/setup'> = {
  add_photos: '/me/photos',
  add_photo: '/me/photos',
  add_bio: '/me/edit',
  extend_bio: '/me/edit',
  add_interests: '/me/edit',
  add_occupation: '/me/edit',
  add_languages: '/me/edit',
  set_city: '/me/edit',
  add_goals: '/dvizh/setup',
  add_preferences: '/dvizh/setup',
}

const HINT_ICON: Record<string, 'image' | 'edit' | 'settings' | 'globe' | 'pin'> = {
  add_photos: 'image',
  add_photo: 'image',
  add_bio: 'edit',
  extend_bio: 'edit',
  add_interests: 'edit',
  add_occupation: 'edit',
  add_languages: 'globe',
  set_city: 'pin',
  add_goals: 'settings',
  add_preferences: 'settings',
}

export function ProfileScreen(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const me = useAppStore((s) => s.me)
  const [hintsOpen, setHintsOpen] = useState(false)

  if (!me)
    return (
      <Screen title="Мой профиль" back>
        {null}
      </Screen>
    )

  const profile = me.profile
  const visibility = profileVisibility(me)
  const attention = profile.photos.filter((p) => p.state === 'rejected')

  /** U-15: своя ссылка вида /@никнейм — как «Поделиться контактом» в Telegram. */
  const shareProfile = async (): Promise<void> => {
    const url = `${location.origin}/@${profile.username ?? ''}`
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: profile.display_name ?? 'Движ', url })
        return
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.show('Ссылка скопирована')
    } catch {
      toast.show(url)
    }
  }

  return (
    <Screen
      title="Мой профиль"
      back
      narrow
      actions={
        <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/me/edit' })}>
          Изменить
        </Button>
      }
    >
      <ProfileHero
        name={profile.display_name}
        age={profile.age}
        photos={profile.photos}
        status={visibility.visible ? 'Анкету показывают' : visibility.title}
        statusTone={visibility.visible ? 'success' : 'warning'}
      />

      {/* Карточка появляется только когда есть что исправить */}
      {!visibility.visible ? <VisibilityCard /> : null}

      <ProfileActions>
        <ProfileAction icon="image" label="Фото" onClick={() => void navigate({ to: '/me/photos' })} />
        {/* U-02: показать анкету так, как её видят другие */}
        <ProfileAction icon="eye" label="Как видят" onClick={() => void navigate({ to: '/me/preview' })} />
        {profile.username ? (
          <ProfileAction icon="share" label="Поделиться" onClick={() => void shareProfile()} />
        ) : (
          <ProfileAction icon="at" label="Никнейм" onClick={() => void navigate({ to: '/me/username' })} />
        )}
      </ProfileActions>

      {/* Карточка сведений: подпись сверху, значение снизу — как в профиле Telegram */}
      <ProfileInfo>
        <ProfileInfoRow
          label="никнейм"
          value={profile.username ? `@${profile.username}` : 'Задать никнейм'}
          accent
          onClick={() => void navigate({ to: '/me/username' })}
        />
        {profile.bio ? (
          <ProfileInfoRow
            label="о себе"
            value={profile.bio}
            multiline
            note={profile.field_states.bio.state === 'pending' ? 'на проверке' : undefined}
            onClick={() => void navigate({ to: '/me/edit' })}
          />
        ) : null}
        {profile.city ? (
          <ProfileInfoRow
            label="город"
            value={profile.city.name}
            onClick={() => void navigate({ to: '/me/edit' })}
          />
        ) : null}
        {profile.occupation ? (
          <ProfileInfoRow
            label="род занятий"
            value={profile.occupation}
            note={profile.field_states.occupation.state === 'pending' ? 'на проверке' : undefined}
            onClick={() => void navigate({ to: '/me/edit' })}
          />
        ) : null}
        {profile.languages.length ? (
          <ProfileInfoRow
            label="языки"
            value={profile.languages.join(', ')}
            onClick={() => void navigate({ to: '/me/edit' })}
          />
        ) : null}
      </ProfileInfo>

      {profile.interests.length ? (
        <ProfileSection title="Интересы">
          <ProfileChips>
            {profile.interests.map((interest) => (
              <Chip key={interest.id} static>
                {interest.label}
              </Chip>
            ))}
          </ProfileChips>
        </ProfileSection>
      ) : null}

      {/*
       * Фотографии показываются не «на всякий случай», а когда о них есть что
       * сказать: фото сняли по проверке. Остальное — на экране «Фото».
       */}
      {attention.length ? (
        <ProfileSection
          title="Фотографии"
          action={
            <Button size="sm" variant="ghost" onClick={() => void navigate({ to: '/me/photos' })}>
              Изменить
            </Button>
          }
        >
          <div className={styles['photos']}>
            {attention.map((photo) => {
              const url = photo.variants.find((v) => v.name === 'medium')?.url ?? photo.variants[0]?.url
              const reason = photoRejectionText(photo)
              return (
                <div key={photo.id} className={styles['photoTile']} title={reason ?? undefined}>
                  {url ? <img src={url} alt="" /> : null}
                  <span className={cx(styles['photoState'], styles['stateRejected'])}>
                    {PHOTO_REJECTED_LABEL}
                  </span>
                </div>
              )
            })}
          </div>
        </ProfileSection>
      ) : null}

      {/* U-08: у заполненного профиля строка не нужна — экран короче */}
      {profile.completeness.percent < 100 ? (
        <div className={styles['rowsCard']}>
          <button type="button" className={styles['listRow']} onClick={() => setHintsOpen(true)}>
            <span className={styles['listLabel']}>Дополнить профиль</span>
            <span className={styles['listValue']}>{profile.completeness.percent}%</span>
            <Icon name="chevron-right" size={18} />
          </button>
        </div>
      ) : null}

      {/* --- U-08 «Дополнить профиль» --- */}
      <Sheet open={hintsOpen} onClose={() => setHintsOpen(false)} title="Дополнить профиль" flush>
        {profile.completeness.hints.length ? (
          <ActionList>
            {profile.completeness.hints.map((hint) => {
              const target = HINT_TARGET[hint] ?? '/me/edit'
              return (
                <ActionItem
                  key={hint}
                  icon={HINT_ICON[hint] ?? 'edit'}
                  label={hintText(hint)}
                  onClick={() => {
                    setHintsOpen(false)
                    void navigate({ to: target })
                  }}
                />
              )
            })}
          </ActionList>
        ) : (
          <p className={styles['hintsDone']}>Добавить нечего — профиль заполнен.</p>
        )}
      </Sheet>
    </Screen>
  )
}
